import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Paper,
  IconButton,
  Divider,
  Link,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  useMediaQuery,
  alpha,
  GlobalStyles,
  ListItemButton,
  InputBase,
  CircularProgress} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  Business,
  ContactSupport,
  Email,
  ExpandLess,
  ExpandMore,
  LocationOn,
  Person,
  Phone,
  Search,
  Verified,
  ViewModule,
  ViewList
} from "@mui/icons-material";
import dealerBg from '../../assets/dealer.webp';
import dealerApi from '../../services/dealerApi';

function Dealers() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [selectedDzongkhag, setSelectedDzongkhag] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDzongkhag, setExpandedDzongkhag] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [dzongkhags, setDzongkhags] = useState([]);
  const [error, setError] = useState(null);

  // Fetch data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await dealerApi.getDealersByDzongkhag();
        setDzongkhags(response);
      } catch (err) {
        console.error('Error fetching dealers:', err);
        setError('Failed to load dealer data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredDzongkhags = dzongkhags
  .filter(dzongkhag => 
    dzongkhag.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
    dzongkhag.dealers.length > 0
  );

  const handleDzongkhagClick = (dzongkhagId) => {
    setExpandedDzongkhag(expandedDzongkhag === dzongkhagId ? null : dzongkhagId);
  };

  const getItemsPerRow = () => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    return 3;
  };

  const itemsPerRow = getItemsPerRow();
  const cardWidth = `calc(${100 / itemsPerRow}% - ${theme.spacing(3)})`;

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        p: 3,
        textAlign: 'center'
      }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: 'background.default',
      minHeight: '100vh',
      pb: 8
    }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: isMobile ? "350px" : "450px",
          backgroundAttachment: isMobile ? 'scroll' : 'fixed',
          backgroundImage: `linear-gradient(135deg, #f8fbff 0%, ${alpha('#e6f2ff', 0.2)} 100%), url(${dealerBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          position: "relative",
          mb: 8,
          px: 2,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '80px',
            background: `linear-gradient(to bottom, transparent, ${theme.palette.background.default})`,
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ 
            textAlign: 'center',
            maxWidth: '800px',
            mx: 'auto',
            animation: 'fadeInUp 0.8s ease'
          }}>
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                lineHeight: 1.2,
                letterSpacing: '-0.5px',
                textShadow: '0 2px 10px rgba(0,0,0,0.2)'
              }}
            >
              Authorized Dealer Network
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              sx={{
                opacity: 0.9,
                mx: 'auto',
                mb: 4,
                fontWeight: 300,
                fontSize: isMobile ? '1rem' : '1.2rem'
              }}
            >
              Discover our trusted partners across Bhutan's districts
            </Typography>
            <Paper 
              component="form"
              sx={{
                p: '2px',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                maxWidth: '600px',
                mx: 'auto',
                borderRadius: '50px',
                bgcolor: 'background.paper',
                boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
              }}
            >
              <InputBase
                placeholder="Search by district..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  ml: 2,
                  flex: 1,
                  py: 1,
                  fontSize: isMobile ? '0.9rem' : '1rem'
                }}
                inputProps={{ 'aria-label': 'search dealers' }}
              />
              <IconButton type="button" sx={{ p: '10px', color: 'primary.main' }} aria-label="search">
                <Search />
              </IconButton>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        {!selectedDzongkhag ? (
          <Box>
            {/* View Mode Toggle */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {filteredDzongkhags.length} Dzongkhags Available
              </Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                aria-label="view mode"
                size="small"
              >
                <ToggleButton value="grid" aria-label="grid view">
                  <ViewModule />
                </ToggleButton>
                <ToggleButton value="list" aria-label="list view">
                  <ViewList />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Districts Display */}
            {viewMode === 'grid' ? (
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                justifyContent: 'center'
              }}>
                {filteredDzongkhags.filter(dzongkhag => dzongkhag.dealers.length).map((dzongkhag) => (
                  <Card 
                    key={dzongkhag.id}
                    sx={{
                      width: cardWidth,
                      minWidth: 250,
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: 2,
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                        borderColor: 'primary.light'
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 2
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {dzongkhag.name}
                        </Typography>
                        <Chip
                          label={`${dzongkhag.dealers.length} ${dzongkhag.dealers.length === 1 ? 'dealer' : 'dealers'}`}
                          size="medium"
                          color="primary"
                          variant="outlined"
                          sx={{
                            pointerEvents: 'none',
                            '&:hover': {
                              backgroundColor: 'inherit',
                            },
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          endIcon={expandedDzongkhag === dzongkhag.id ? <ExpandLess /> : <ExpandMore />}
                          onClick={() => handleDzongkhagClick(dzongkhag.id)}
                          sx={{
                            justifyContent: 'space-between',
                            textTransform: 'none',
                            py: 1,
                            borderRadius: '8px'
                          }}
                        >
                          {expandedDzongkhag === dzongkhag.id ? 'Hide dealers' : 'View dealers'}
                        </Button>
                      </Box>

                      <Collapse in={expandedDzongkhag === dzongkhag.id}>
                        <List dense sx={{ py: 0 }}>
                          {dzongkhag.dealers.slice(0, 3).map((dealer) => (
                            <ListItem 
                              key={dealer.id} 
                              disableGutters
                              sx={{
                                py: 1,
                                px: 0,
                                '&:not(:last-child)': {
                                  borderBottom: '1px solid',
                                  borderColor: 'divider'
                                }
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: '36px', color: 'primary.main' }}>
                                <Business fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {dealer.name}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary">
                                    {dealer.contactPerson}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                          {dzongkhag.dealers.length > 3 && (
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              display: 'block', 
                              textAlign: 'center',
                              mt: 1
                            }}>
                              +{dzongkhag.dealers.length - 3} more
                            </Typography>
                          )}
                        </List>
                      </Collapse>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'center', p: 2 }}>
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        endIcon={<ArrowForward />}
                        onClick={() => setSelectedDzongkhag(dzongkhag)}
                        sx={{
                          borderRadius: '20px',
                          textTransform: 'none',
                          py: 1
                        }}
                      >
                        View Details
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            ) : (
              <Paper elevation={0} sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden'
              }}>
                <List disablePadding>
                  {filteredDzongkhags.filter(dzongkhag => dzongkhag.dealers.length).map((dzongkhag) => (
                    <React.Fragment key={dzongkhag.id}>
                      <ListItemButton
                        onClick={() => handleDzongkhagClick(dzongkhag.id)}
                        sx={{
                          py: 3,
                          px: 3,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {dzongkhag.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {dzongkhag.dealers.length} authorized {dzongkhag.dealers.length === 1 ? 'dealer' : 'dealers'}
                          </Typography>
                        </Box>
                        {expandedDzongkhag === dzongkhag.id ? 
                          <ExpandLess color="primary" /> : 
                          <ExpandMore color="primary" />}
                      </ListItemButton>
                      
                      <Collapse in={expandedDzongkhag === dzongkhag.id}>
                        <Box sx={{ 
                          p: 3,
                          pt: 0,
                          backgroundColor: 'background.paper'
                        }}>
                          <Grid container spacing={2}>
                            {dzongkhag.dealers.map((dealer) => (
                              <Grid item xs={12} sm={6} key={dealer.id}>
                                <Paper 
                                  variant="outlined"
                                  sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    '&:hover': {
                                      borderColor: 'primary.main',
                                      cursor: 'pointer'
                                    }
                                  }}
                                  onClick={() => setSelectedDzongkhag(dzongkhag)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    {dealer.verified && (
                                      <Verified 
                                        color="primary" 
                                        sx={{ 
                                          mr: 1,
                                          fontSize: '18px'
                                        }} 
                                      />
                                    )}
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      {dealer.name}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {dealer.address}
                                  </Typography>
                                  <Typography variant="caption" sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'primary.main'
                                  }}>
                                    <Person fontSize="small" sx={{ mr: 0.5 }} />
                                    {dealer.contactPerson}
                                  </Typography>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                          <Box sx={{ mt: 2, textAlign: 'right' }}>
                            <Button 
                              variant="text" 
                              size="small" 
                              endIcon={<ArrowForward />}
                              onClick={() => setSelectedDzongkhag(dzongkhag)}
                              sx={{
                                textTransform: 'none',
                                color: 'primary.main'
                              }}
                            >
                              View all details
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        ) : (
          <Box>
            <Button
              variant="text"
              startIcon={<ArrowBack fontSize="small" />}
              onClick={() => setSelectedDzongkhag(null)}
              sx={{
                mb: 3,
                px: 1,
                py: 0.5,
                color: 'grey.600',
                fontSize: '0.875rem',
                fontWeight: 500,
                fontFamily: 'inherit',
                '&:hover': {
                  color: 'primary.main', 
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  textDecoration: 'underline',
                  textDecorationColor: 'primary.main',
                  textUnderlineOffset: '3px',
                  '& .MuiSvgIcon-root': {
                    color: 'primary.main'
                  }
                },
                '&:active': {
                  color: 'primary.dark',
                  backgroundColor: 'rgba(25, 118, 210, 0.08)'
                },
                '& .MuiButton-startIcon': {
                  marginRight: '8px',
                  marginLeft: '-4px',
                  transition: 'color 0.2s ease-out'
                },
                transition: 'all 0.2s ease-out'
              }}
            >
              back to list
            </Button>

            <Box sx={{ 
              mb: 6,
              animation: 'fadeIn 0.5s ease'
            }}>
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                mb: 3
              }}>
                <Box>
                  <Typography variant="h4" component="h2" sx={{ 
                    fontWeight: 700,
                    mb: 1
                  }}>
                    {selectedDzongkhag.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Authorized dealers and distributors in this district
                  </Typography>
                </Box>
                <Chip
                  label={`${selectedDzongkhag.dealers.length} ${selectedDzongkhag.dealers.length === 1 ? 'dealer' : 'dealers'}`}
                  size="medium"
                  color="primary"
                  variant="outlined"
                  sx={{
                    pointerEvents: 'none',
                    '&:hover': {
                      backgroundColor: 'inherit',
                    },
                  }}
                />
              </Box>
              
              <Divider sx={{ mb: 4 }} />
              
              <Grid container spacing={3}>
                {selectedDzongkhag.dealers.map((dealer) => (
                  <Grid item xs={12} sm={6} md={4} key={dealer.id}>
                    <Card sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 4
                      }
                    }}>
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 2 
                        }}>
                          {dealer.verified && (
                            <Tooltip title="Verified Dealer" arrow>
                              <Box sx={{
                                backgroundColor: 'primary.light',
                                borderRadius: '50%',
                                p: 0.5,
                                mr: 1.5,
                                display: 'flex'
                              }}>
                                <Verified color="primary" sx={{ fontSize: '20px' }} />
                              </Box>
                            </Tooltip>
                          )}
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {dealer.name}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', mb: 1.5 }}>
                            <LocationOn sx={{ 
                              color: 'text.secondary',
                              mr: 1.5,
                              mt: 0.5 
                            }} />
                            <Typography variant="body2">
                              {dealer.address}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', mb: 1.5 }}>
                            <Person sx={{ 
                              color: 'text.secondary',
                              mr: 1.5,
                              mt: 0.5 
                            }} />
                            <Typography variant="body2">
                              <Box component="span" sx={{ fontWeight: 500 }}>Contact:</Box> {dealer.contactPerson}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Paper elevation={0} sx={{ 
                          backgroundColor: 'action.hover',
                          p: 2,
                          borderRadius: 1
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Phone sx={{ 
                              color: 'primary.main',
                              mr: 1.5 
                            }} />
                            <Link href={`tel:${dealer.phone}`} underline="hover" color="inherit">
                              {dealer.phone}
                            </Link>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Email sx={{ 
                              color: 'primary.main',
                              mr: 1.5 
                            }} />
                            <Link href={`mailto:${dealer.email}`} underline="hover" color="inherit">
                              {dealer.email}
                            </Link>
                          </Box>
                        </Paper>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
            
            {/* Support Section */}
            <Paper elevation={4} sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 90%)`,
              color: 'primary.contrastText',
              mb: 4,
              overflow: 'hidden'
            }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6} sx={{
                  pr: { md: 3 },
                  borderRight: { md: `1px solid ${alpha(theme.palette.common.white, 0.2)}` }
                }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    Can't find what you're looking for?
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 3,
                    opacity: 0.9,
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}>
                    Our team is ready to assist you in finding the perfect dealer or answering any questions about our products and services.
                  </Typography>
                  
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    startIcon={<ContactSupport />}
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: '50px',
                      textTransform: 'none',
                      px: 4,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >
                    Contact Support
                  </Button>
                </Grid>

                <Grid item xs={12} md={6} sx={{ pl: { md: 3 } }}>
                  <Box sx={{ 
                    backgroundColor: alpha('#fff', 0.1),
                    p: { xs: 2, sm: 3 },
                    borderRadius: 1,
                    backdropFilter: 'blur(10px)',
                    mt: { xs: 2, md: 0 }
                  }}>
                    {/* Phone Info with Copy */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 3,
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}>
                      <Box sx={{
                        backgroundColor: alpha('#fff', 0.2),
                        borderRadius: '50%',
                        p: { xs: 1, sm: 1.5 },
                        mr: { sm: 2 },
                        mb: { xs: 1, sm: 0 },
                        display: 'flex',
                        flexShrink: 0
                      }}>
                        <Phone sx={{ fontSize: { xs: '24px', sm: '28px' } }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ 
                          display: 'block',
                          opacity: 0.8,
                          mb: 0.5,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}>
                          Contact Support
                        </Typography>
                        <Tooltip title="Click to copy" arrow>
                          <Box 
                            onClick={() => {
                              navigator.clipboard.writeText('+97517171136');
                            }}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            <Typography variant="h6" sx={{ 
                              fontWeight: 600,
                              fontSize: { xs: '1rem', sm: '1.125rem' }
                            }}>
                              (+975) 17171136
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    {/* Email Info with Copy */}
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}>
                      <Box sx={{
                        backgroundColor: alpha('#fff', 0.2),
                        borderRadius: '50%',
                        p: { xs: 1, sm: 1.5 },
                        mr: { sm: 2 },
                        mb: { xs: 1, sm: 0 },
                        display: 'flex',
                        flexShrink: 0
                      }}>
                        <Email sx={{ fontSize: { xs: '24px', sm: '28px' } }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ 
                          display: 'block',
                          opacity: 0.8,
                          mb: 0.5,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}>
                          Email Support
                        </Typography>
                        <Tooltip title="Click to copy" arrow>
                          <Box 
                            onClick={() => {
                              navigator.clipboard.writeText('koufukucustomercare@gmail.com');
                            }}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            <Typography variant="h6" sx={{ 
                              fontWeight: 600,
                              fontSize: { xs: '0.9rem', sm: '1.125rem' },
                              wordBreak: 'break-word'
                            }}>
                              koufukucustomercare@gmail.com
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}
      </Container>

      <GlobalStyles styles={{
        '@keyframes fadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 }
        },
        '@keyframes fadeInUp': {
          '0%': { 
            opacity: 0,
            transform: 'translateY(20px)'
          },
          '100%': { 
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }} />
    </Box>
  );
}

export default Dealers;